/** Hooks TanStack Query pour la gestion des employés */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { employeesApi } from '../services/api';
import type { EmployeeCreate, EmployeeUpdate } from '../types';

export function useEmployees(params: {
  page?: number;
  per_page?: number;
  search?: string;
  active_only?: boolean;
  site_id?: number;
  activity?: 'boulangerie' | 'traiteur' | 'chambre_froide' | 'cleaning' | 'driving' | 'office';
}) {
  return useQuery({
    queryKey: ['employees', params],
    queryFn: () => employeesApi.list(params),
    placeholderData: (prev) => prev,
  });
}

export function useEmployee(id: number) {
  return useQuery({
    queryKey: ['employee', id],
    queryFn: () => employeesApi.get(id),
  });
}

export function useCreateEmployee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: EmployeeCreate) => employeesApi.create(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['employees'] }),
  });
}

export function useUpdateEmployee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: EmployeeUpdate }) =>
      employeesApi.update(id, data),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ['employees'] });
      qc.invalidateQueries({ queryKey: ['employee', id] });
    },
  });
}

export function useDeleteEmployee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => employeesApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['employees'] }),
  });
}

export function useAssignBadge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, badge_uid }: { id: number; badge_uid: string }) =>
      employeesApi.assignBadge(id, badge_uid),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ['employees'] });
      qc.invalidateQueries({ queryKey: ['employee', id] });
    },
  });
}

export function useRemoveBadge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => employeesApi.removeBadge(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ['employees'] });
      qc.invalidateQueries({ queryKey: ['employee', id] });
    },
  });
}
